import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import i18next from 'i18next';
import { basesUrl } from '../../hooks/useHttp';
import styles from './errorBoundary.module.scss';
/** Сколько раз подряд перезагружаем страницу из-за устаревшего бандла. */
const CHUNK_RELOAD_KEY = 'avnvm_chunk_reload';
/**
 * Ошибка при отрисовке страницы.
 *
 * Без перехватчика React при любом исключении снимает с экрана всё
 * приложение, и человек видит пустой тёмный фон — ни меню, ни текста.
 * Так было у преподавателя ПК МУКР (@51088/26): «Сегодня» и журнал дня
 * оставались чёрными, в логе сервера при этом ни одной ошибки — всё
 * случилось в браузере, и узнать, что именно, было не из чего.
 *
 * Теперь вместо пустоты — сообщение и кнопка «Обновить», а текст ошибки
 * со стеком уходит на сервер и попадает в лог модуля у вуза.
 */
class ErrorBoundary extends React.Component {
    constructor() {
        super(...arguments);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // После выкладки новой версии старая вкладка просит у сервера куски
        // бандла, которых уже нет. Лечится одной перезагрузкой — делаем её
        // сами, но не чаще раза в минуту, чтобы не уйти в бесконечный цикл.
        if (isChunkError(error)) {
            let tried = true;
            try {
                const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
                tried = Date.now() - last < 60000;
                sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
            }
            catch (e) {
                // хранилище недоступно — без перезагрузки, просто покажем сообщение
            }
            if (!tried) {
                window.location.reload();
                return;
            }
        }
        reportError(this.props.place, error, info.componentStack || '');
    }
    render() {
        const { error } = this.state;
        if (!error)
            return this.props.children;
        return (_jsx("div", Object.assign({ className: styles.wrap, role: "alert" }, { children: _jsxs("div", Object.assign({ className: styles.card }, { children: [_jsx("h2", Object.assign({ className: styles.title }, { children: tr('page_failed_title', 'Страница не открылась') })), _jsx("p", Object.assign({ className: styles.text }, { children: tr('page_failed_text', 'Обновите страницу. Если не помогло — сообщите в поддержку время и что вы нажимали: ошибка уже записана.') })), _jsx("code", Object.assign({ className: styles.detail }, { children: String(error.message || error).slice(0, 300) })), _jsx("button", Object.assign({ type: "button", className: styles.reload, onClick: () => window.location.reload() }, { children: tr('page_failed_reload', 'Обновить') }))] })) })));
    }
}
/** Перевод с запасным текстом: переводы могли и не загрузиться. */
const tr = (key, fallback) => String(i18next.t(key, { defaultValue: fallback }));
function isChunkError(error) {
    return (error === null || error === void 0 ? void 0 : error.name) === 'ChunkLoadError' || /Loading (CSS )?chunk \d+ failed/i.test(String(error === null || error === void 0 ? void 0 : error.message));
}
/** Последняя отправленная ошибка — React повторяет сбой, лог не должен. */
let lastReport = { key: '', at: 0 };
/** Отправить ошибку в лог модуля. Сбой отправки ничего не ломает. */
function reportError(place, error, componentStack) {
    const key = `${window.location.pathname}|${String((error === null || error === void 0 ? void 0 : error.message) || error)}`;
    if (key === lastReport.key && Date.now() - lastReport.at < 10000)
        return;
    lastReport = { key, at: Date.now() };
    try {
        fetch(`${basesUrl}/client-error`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                place,
                url: window.location.pathname,
                message: String((error === null || error === void 0 ? void 0 : error.message) || error),
                stack: String((error === null || error === void 0 ? void 0 : error.stack) || ''),
                componentStack,
                userAgent: navigator.userAgent
            })
        }).catch(() => undefined);
    }
    catch (e) {
        // отправка ошибки не должна сама стать ошибкой
    }
}
export default ErrorBoundary;
